import React, { ChangeEvent, FC, ReactNode, useEffect, useMemo, useState } from 'react';
import { Input, Tooltip } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import Sparkle from '../../public/icons/sparkle.svg';
import Image from 'next/image';

type ActionInputWithHelperProps = {
  value?: string;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  helperPrefix?: string;
  helperText?: string;
  showInfo?: boolean;
  actionText?: string;
  onAction?: () => void;

  disabled?: boolean;
  loading?: boolean;
  actionDisabled?: boolean;
  className?: string;
  infoTooltip?: ReactNode;
  variant?: 'default' | 'disabled';
};

export const ActionInputWithHelper: FC<ActionInputWithHelperProps> = (props) => {
  const {
    value,
    onChange,
    placeholder,
    helperPrefix = 'New!',
    helperText = 'Helper action',
    showInfo = true,
    actionText = 'Try now',
    onAction,
    disabled,
    loading,
    actionDisabled,
    className = '',
    infoTooltip,
    variant = 'default',
  } = props;
  const [activeDot, setActiveDot] = useState(0);
  const dotIndexes = useMemo(() => [0, 1, 2], []);

  useEffect(() => {
    if (!loading) {
      setActiveDot(0);
      return;
    }
    const interval = setInterval(() => {
      setActiveDot((prev) => (prev + 1) % dotIndexes.length);
    }, 350);
    return () => clearInterval(interval);
  }, [dotIndexes.length, loading]);

  const isDisabledVariant = variant === 'disabled';
  const isActionDisabled = disabled || actionDisabled || !onAction || isDisabledVariant;
  const tooltipMessage =
    infoTooltip ?? (isDisabledVariant ? 'Feature available for login' : 'More info');
  const sparkleTone = isDisabledVariant ? 'opacity-40' : '';
  const helperChipClass = [
    'mt-2 inline-flex flex-wrap items-center gap-2 rounded-full px-3 py-1',
    isDisabledVariant
      ? 'bg-gray-100 text-gray-500 border border-gray-200'
      : 'bg-violet-50 text-violet-700 border border-transparent',
  ].join(' ');
  const actionButtonClass = [
    'h-10 px-auto py-auto !rounded-full px-3 border shadow-none',
    isDisabledVariant
      ? 'border-gray-200 !bg-gray-50 text-gray-500 cursor-not-allowed'
      : 'border-violet-200 !bg-white text-violet-700 cursor-pointer',
    isDisabledVariant
      ? '!hover:bg-gray-50 !hover:text-gray-500'
      : '!hover:text-violet-700 !hover:bg-violet-50 !hover:border-violet-300',
    isDisabledVariant
      ? '!active:bg-gray-100 !active:border-gray-300'
      : '!active:bg-violet-100 !active:border-violet-400',
  ].join(' ');

  return (
    <div className={className}>
      <Input value={value} onChange={onChange} placeholder={placeholder} disabled={disabled} />

      <div className={helperChipClass}>
        <span className="text-sm font-medium">
          {helperPrefix} <span className="font-normal">{helperText}</span>
        </span>

        {showInfo && (
          <Tooltip title={tooltipMessage}>
            <InfoCircleOutlined
              className={`text-xs ${isDisabledVariant ? 'text-gray-500' : 'text-violet-600'}`}
            />
          </Tooltip>
        )}
        <button
          type="button"
          onClick={() => onAction?.()}
          disabled={isActionDisabled}
          className={actionButtonClass}>
          <div className="flex flex-row justify-center items-center">
            {loading ? (
              <div className="flex items-center gap-2">
                <Image
                  className={`animate-pulse ${sparkleTone}`}
                  src={Sparkle}
                  alt=""
                  width={20}
                  height={20}
                />
                <span className="flex items-center gap-1">
                  {dotIndexes.map((i) => (
                    <span
                      key={i}
                      className="h-1.5 w-1.5 rounded-full bg-violet-600 transition-opacity duration-200"
                      style={{ opacity: activeDot === i ? 1 : 0.25 }}
                    />
                  ))}
                </span>
              </div>
            ) : (
              <>
                <p className="m-0">{actionText}</p>
                <Image
                  className={`ml-2 mt-0.5 ${sparkleTone}`}
                  src={Sparkle}
                  alt=""
                  width={20}
                  height={20}
                />
              </>
            )}
          </div>
        </button>
      </div>
    </div>
  );
};
